/**
 * Typed API client for the admin backend.
 *
 * - Base URL from VITE_API_BASE_URL env var (default: http://localhost:3001)
 * - Unwraps the { data } envelope from successful responses
 * - Throws typed errors with validation details from 400s
 */

import type {
  AdvertiserResponse,
  CampaignAggregateCreateInput,
  CampaignAggregateUpdateInput,
  CampaignAssetUploadResponse,
  CampaignLinkResponse,
  CampaignResponse,
  CaptureRejectionResponse,
  CreateAdvertiser,
  CreateCampaignLinkInput,
  CreateEmailAllowList,
  CreatePromoType,
  CreatePublisher,
  CreateSite,
  CtoApprovedDomains,
  CtoApprovedDomainsUpdate,
  EmailAllowListResponse,
  LeadListRowResponse,
  PromoTypeResponse,
  PublisherResponse,
  SiteResponse,
  SuppressionFileListRowResponse,
  SuppressionFileResponse,
  UpdateCampaignLinkInput,
  UpdatePromoType,
} from '@leads-platform/shared';

/**
 * One CTO link row as the admin api returns it: the shared campaign-link read
 * payload plus the display fields the row renders (site label, the site's
 * business key — the `utm_siteid` value — and the promo slug + name). The api
 * module types the same superset next to its mapper; folding the four fields
 * into `campaignLinkResponseSchema` is a one-line shared edit for the tail.
 */
export type CampaignLinkRow = CampaignLinkResponse & {
  siteLabel: string;
  utmSiteId: string;
  promoSlug: string;
  promoName: string;
};

/**
 * One CTO link row in the cross-campaign directory (task 064-01, `GET
 * /admin/campaign-links`): the same superset {@link CampaignLinkRow} carries,
 * plus the owning campaign's name and status — the directory has no single
 * campaign of its own to supply them. Mirrors the api's api-local
 * `CampaignLinkDirectoryRowResponse` (`campaign-link.mapper.ts`), same
 * precedent as `CampaignLinkRow` above.
 */
export type CampaignLinkDirectoryRow = CampaignLinkRow & {
  campaignName: string;
  campaignStatus: CampaignResponse['status'];
};

/**
 * One page in the Page Order overview (task 071-01, `GET /admin/page-order`):
 * a campaign and where it sits in the offer walk. `sequence` is null on a
 * "Not placed" row, and `spot` is null there too, or on a placed page with no
 * spot. `liveToday` is the walk's status and day rule, not a per-visitor
 * verdict. Mirrors the api's api-local `PageOrderRowResponse`
 * (`page-order.service.ts`), same precedent as `CampaignLinkDirectoryRow`.
 */
export type PageOrderRow = {
  campaignId: string;
  campaignName: string;
  /** The hosted page's slug, null when the campaign has none — mirrors the api's rule. */
  pageSlug: string | null;
  campaignStyle: CampaignResponse['campaignStyle'];
  status: CampaignResponse['status'];
  startDate: string | null;
  endDate: string | null;
  archived: boolean;
  sequence: number | null;
  spot: number | null;
  liveToday: boolean;
  /** 1-based place in its sequence's order; null on a Not placed or NULL-spot row (epic 077 D10). */
  position: number | null;
  /** The sequence's pin names this page and it has a spot there (epic 077 D7). */
  pinned: boolean;
  /** The latest nightly ranking's line for this page, else null (epic 077 D9). */
  rank: PageRank | null;
  /** No price or an unreadable send mode: the ranking counts the page as $0 (epic 077 D2). */
  priceMissing: boolean;
};

/**
 * One page's line of the latest nightly ranking (epic 077 D9): its accepts,
 * skips, revenue and revenue per response (null with no response) over the
 * 7-day window, and whether it has enough responses to be ranked.
 */
export type PageRank = {
  accepts: number;
  skips: number;
  responses: number;
  revenue: number;
  revenuePerResponse: number | null;
  ranked: boolean;
};

/**
 * The ranking the order was built on (epic 077 D3): its day and 7-day window
 * as `yyyy-MM-dd` text, and the responses a page needs to be ranked.
 */
export type PageOrderRanking = {
  rankedOn: string;
  windowFrom: string;
  windowTo: string;
  minResponses: number;
};

/**
 * The Page Order overview: the walk master switch, the sequences each
 * audience walks, the order mode (`OFFERS_WALK_ORDER`, `value` since epic
 * 098), the latest ranking (null before the first nightly run) and every row
 * in the API's display order: each sequence in the order visitors see it.
 * Mirrors the api's `PageOrderResponse`.
 */
export type PageOrderResponse = {
  walkEnabled: boolean;
  paidSocialSequence: number;
  ctoSequence: number;
  walkOrder: 'revenue' | 'spot' | 'value';
  ranking: PageOrderRanking | null;
  rows: PageOrderRow[];
  /**
   * HF-146: every not-yet-archived completed custom page, one list, no spots
   * (name order, then id) — the Completed card at the bottom of the screen.
   * These rows never appear in `rows`: a completed page has left every
   * sequence card.
   */
  completedRows: PageOrderRow[];
  /** The value-mode stats (epic 098 D3); null in every other mode, and in value mode before the first estimate run. */
  valueStats: PageOrderValueStats | null;
};

/**
 * One placed page's value estimate for one display audience (epic 098 D3):
 * the Beta posterior the walk samples from, the same `estimateFor` resolution
 * the walk uses (the audience's row, else the page's `all` row). Mirrors the
 * api's `ValueEstimateRow` (`page-order-value.stats.ts`).
 */
export interface PageOrderValueEstimateRow {
  campaignId: string;
  /** `all` pools every visitor outside a split style's own audience (092 D11). */
  audience: 'all' | 'cto_email' | 'paid_social';
  /** The Beta mean α/(α+β), 0..1. */
  mean: number;
  /** The Beta sd, in the same 0..1 units as `mean`. */
  sd: number;
  alpha: number;
  beta: number;
}

/**
 * One page's share of first slots for one display audience (epic 098 D2, D3),
 * from the newest nightly aggregate day. Mirrors the api's
 * `FirstShareRowStats` (`page-order-value.stats.ts`).
 */
export interface PageOrderFirstShareRow {
  campaignId: string;
  audience: 'all' | 'cto_email' | 'paid_social';
  /** Value-arm walks that saw the page that day. */
  walks: number;
  /** Views at position 1 — the numerator of the share of first slots. */
  firstShows: number;
  /** Views at position 3 or shallower. */
  top3Shows: number;
  /** `firstShows / max(walks, 1)` — 0 for a page with no walks. */
  share: number;
}

/** One arm's tally for one day of the readout card (a stored run row's `arms` entry). */
export interface PageOrderArmDay {
  visitors: number;
  billableAccepts: number;
  revenue: number;
  /** Null when the day had visitors but no readable per-visitor revenue. */
  revenuePerVisitor: number | null;
}

/**
 * One day of the holdout comparison (epic 098 D2): a null arm has no readable
 * tally for that day. Mirrors the api's `ArmsHistoryDay`
 * (`page-order-value.stats.ts`).
 */
export interface PageOrderArmsHistoryDay {
  /** The ET day, `yyyy-MM-dd`. */
  day: string;
  value: PageOrderArmDay | null;
  holdout: PageOrderArmDay | null;
}

/**
 * The value-mode stats block of the Page Order response (epic 098 D3).
 * Mirrors the api's `PageOrderValueStats` (`page-order-value.stats.ts`).
 */
export interface PageOrderValueStats {
  /** The estimate run's day, `yyyy-MM-dd`. */
  estimatedOn: string;
  /** Per placed page, per display audience; pages without a resolution's row are left out. */
  rows: PageOrderValueEstimateRow[];
  /** The newest first-share day; null before the first nightly write. */
  firstShare: { day: string; rows: PageOrderFirstShareRow[] } | null;
  /** The last 14 run days' per-arm tallies, newest first. */
  armsHistory: PageOrderArmsHistoryDay[];
}

/**
 * One page in a Page Order reorder (task 071-03, `PUT
 * /admin/page-order/sequences/:sequence`): the campaign, and the spot the
 * editor loaded it at, so the api can 409 a sequence that changed since.
 */
export type PageReorderItem = {
  campaignId: string;
  expectedSpot: number | null;
};

/**
 * One capture-rejection row as the admin api returns it (the leads screen's
 * Rejected view, epic 024 task 14): the shared capture-rejection read payload
 * narrowed to the list columns plus the two display joins — the campaign name
 * and the CTO link label (`"<site label> · <promo slug>"`). The api module
 * types the same superset next to its mapper; folding the two display fields
 * into `captureRejectionResponseSchema` is a one-line shared edit for the tail.
 */
export type CaptureRejectionRow = Pick<
  CaptureRejectionResponse,
  | 'id'
  | 'emailOriginal'
  | 'campaignId'
  | 'campaignLinkId'
  | 'siteId'
  | 'reason'
  | 'country'
  | 'isTest'
  | 'createdAt'
> & {
  campaignName: string | null;
  linkLabel?: string;
};

/** Rejection list — rows + total, the same envelope shape as the leads list. */
export interface CaptureRejectionList {
  rows: CaptureRejectionRow[];
  total: number;
}

/**
 * One lead row as the admin api returns it: the shared lead-list read payload
 * plus the display `linkLabel` (the CTO link's `"<site label> · <promo slug>"`,
 * epic 024 task 14). The api module types the same superset next to its
 * mapper; folding it into `leadListRowResponseSchema` is a one-line shared edit
 * for the tail.
 */
export type LeadListRow = LeadListRowResponse & { linkLabel?: string };

/**
 * Lead list — rows + total, the shared envelope shape with the widened row,
 * plus `people` (the filtered set's distinct addresses, epic 088 D7).
 */
export interface LeadListWithLink {
  rows: LeadListRow[];
  total: number;
  people: number;
}

/** Per-link stats row for the date-range stats panel (task 024-09). */
export interface CampaignLinkStat {
  linkId: string;
  leads: number;
  uniqueEmails: number;
  success: number;
  duplicate: number;
  unchecked: number;
  rejected: number;
}

/** Response from GET /admin/campaigns/:id/links/stats. */
export interface CampaignLinkStatsResponse {
  from: string;
  to: string;
  timezone: string;
  links: CampaignLinkStat[];
  campaign: { leads: number; uniqueEmails: number; rejected: number };
}

/** Per-site stats row for the Publisher uniques panel (task 024-09). */
export interface SiteStat {
  id: string;
  siteId: string;
  label: string;
  leads: number;
  uniqueEmails: number;
  rejected: number;
}

/** Response from GET /admin/sites/stats. */
export interface SiteStatsResponse {
  from: string;
  to: string;
  timezone: string;
  sites: SiteStat[];
}

/**
 * Response of POST /admin/preview-drafts (epic 037 task 05) — the short-lived
 * token naming the stored draft. Schema defined here (not in shared): the
 * preview round-trip is a local admin/landing concern and shared is read-only.
 */
export interface PreviewDraftResponse {
  token: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * Campaign read payload of the admin campaigns routes (list + detail): the shared
 * aggregate response plus the route-local `suppressionFileUnreadable` flag
 * (#49 — suppression enabled AND the linked file unreadable on the server, so the
 * walk is skipping suppression for that campaign). The flag is additive on the
 * api side and deliberately not part of the frozen shared campaign schemas.
 */
export type CampaignDetailResponse = CampaignResponse & {
  suppressionFileUnreadable?: boolean;
  /** Archive marker (task 064-04, QA-02): api-local like `suppressionFileUnreadable` above. */
  archivedAt?: string | null;
};

/**
 * Global in-flight request counter. Drives the top LoadingBar so navigation
 * and background refetches show a thin progress bar instead of blanking the
 * work surface to a skeleton placeholder. Every request increments on start
 * and decrements when it settles (success or error).
 */
let inflightRequestCount = 0;
const loadingListeners = new Set<() => void>();

function notifyLoadingListeners(): void {
  for (const fn of loadingListeners) fn();
}

function beginRequest(): void {
  inflightRequestCount += 1;
  notifyLoadingListeners();
}

function endRequest(): void {
  inflightRequestCount = Math.max(0, inflightRequestCount - 1);
  notifyLoadingListeners();
}

/** Subscribe to in-flight changes. Returns an unsubscribe function. */
export function subscribeLoading(fn: () => void): () => void {
  loadingListeners.add(fn);
  return () => {
    loadingListeners.delete(fn);
  };
}

/** Number of API requests currently in flight (0 when idle). */
export function getInflightCount(): number {
  return inflightRequestCount;
}

/**
 * Error thrown when the API returns an error response.
 * Includes validation errors from Zod (400 responses).
 *
 * Backend ZodValidationPipe throws BadRequestException with body
 * `{ message: 'Validation failed', errors: [{path, message, code}, ...] }`,
 * which Nest serializes as `{statusCode, message, error, errors}`. We collapse
 * the `errors` array into a `Record<path, message[]>` for per-field surfacing.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Map a backend validation error array (`{path, message, code}`) into a
 * `Record<path, message[]>`. The root path is normalized to `_form` so the
 * form can surface aggregate-level issues.
 */
function mapValidationErrors(errors: unknown): Record<string, string[]> | undefined {
  if (!Array.isArray(errors)) return undefined;
  const out: Record<string, string[]> = {};
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { path?: string; message?: string };
    const key = e.path && e.path.length > 0 ? e.path : '_form';
    const msg = e.message ?? 'Invalid';
    if (!out[key]) out[key] = [];
    out[key].push(msg);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * API response envelope — matches the backend's ApiResponse<T> shape.
 */
interface ApiResponse<T> {
  data: T;
}

/**
 * Request options for `request()`. `allowEmptyBody` marks endpoints whose
 * successful response may be `204 No Content` (or otherwise an empty body);
 * such calls resolve to `undefined` instead of failing JSON parsing.
 */
interface RequestConfig extends RequestInit {
  allowEmptyBody?: true;
}

/**
 * Options of the shared request wrapper. `skipAuthRedirect` opts one call out
 * of the 401 → `/login` redirect — the auth endpoints themselves (login, me,
 * logout, change-password) use it: they own their unauthenticated UX (a 401
 * from change-password is "current password incorrect", not a dead session).
 */
export interface ApiFetchInit extends RequestInit {
  skipAuthRedirect?: boolean;
}

/**
 * The 401 redirect's navigation act, as a replaceable indirection: jsdom's
 * `window.location` members are [Unforgeable] (unspyable), so the wrapper's
 * unit tests observe this seam instead of the browser. Production always
 * takes the `window.location.assign` branch.
 */
export const loginRedirect = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

/**
 * The one request wrapper every admin-client fetch goes through (epic 076
 * task 03, plan review r1 B-1). The session cookie is the credential — no
 * `Authorization` header, no token storage — so every call:
 *
 * - sends `credentials: 'include'`: the vite dev topology serves the SPA on
 *   `:5173` against the api on `:3001` — cross-origin, where the fetch
 *   default `same-origin` would silently drop the cookie (the api's CORS
 *   already allows credentials, `bootstrap.ts:77-80`);
 * - sends `X-Requested-With: XMLHttpRequest` — the api's session guard
 *   requires it on every mutating request (the CSRF posture with
 *   SameSite=Lax);
 * - on a 401 redirects the browser to `/login?return=…` — except when the
 *   call opts out (`skipAuthRedirect`) and never when already on `/login`
 *   (the loop guard: a logged-out visitor's `me` 401 must stay put, r1 H-3).
 */
export async function apiFetch(url: string, init: ApiFetchInit = {}): Promise<Response> {
  const response = await fetch(url, {
    ...init,
    headers: { 'X-Requested-With': 'XMLHttpRequest', ...init.headers },
    credentials: 'include',
  });
  if (
    response.status === 401 &&
    init.skipAuthRedirect !== true &&
    window.location.pathname !== '/login'
  ) {
    const returnTo = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    loginRedirect.assign(`/login?return=${returnTo}`);
  }
  return response;
}

/**
 * HTTP client wrapper with typed endpoints.
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /** Endpoints with a guaranteed JSON envelope resolve to `T`. */
  private async request<T>(path: string, options?: RequestConfig): Promise<T>;
  /** Endpoints declared with `allowEmptyBody` may resolve to `undefined` (204/empty body). */
  private async request<T>(
    path: string,
    options: RequestConfig & { allowEmptyBody: true }
  ): Promise<T | undefined>;
  private async request<T>(path: string, options?: RequestConfig): Promise<T | undefined> {
    const url = `${this.baseUrl}${ADMIN_PREFIX}${path}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    beginRequest();
    try {
      const response = await apiFetch(url, { ...options, headers });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          typeof errorData.message === 'string' && errorData.message.length > 0
            ? errorData.message
            : response.statusText;
        throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
      }

      // 204/empty bodies (DELETE endpoints) have no envelope — resolve undefined.
      // Non-JSON 200 bodies still throw and surface as ApiError(0, ...) below.
      const text = await response.text();
      if (text.length === 0) {
        return undefined;
      }
      const envelope: ApiResponse<T> = JSON.parse(text);
      return envelope.data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error or invalid JSON');
    } finally {
      endRequest();
    }
  }

  // Advertisers
  async getAdvertisers(): Promise<AdvertiserResponse[]> {
    return this.request<AdvertiserResponse[]>('/advertisers');
  }

  async getAdvertiser(id: string): Promise<AdvertiserResponse> {
    return this.request<AdvertiserResponse>(`/advertisers/${id}`);
  }

  async createAdvertiser(data: CreateAdvertiser): Promise<AdvertiserResponse> {
    return this.request<AdvertiserResponse>('/advertisers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdvertiser(id: string, data: Partial<CreateAdvertiser>): Promise<AdvertiserResponse> {
    return this.request<AdvertiserResponse>(`/advertisers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAdvertiser(id: string): Promise<void> {
    await this.request<void>(`/advertisers/${id}`, {
      method: 'DELETE',
      allowEmptyBody: true,
    });
  }

  // Publishers
  async getPublishers(): Promise<PublisherResponse[]> {
    return this.request<PublisherResponse[]>('/publishers');
  }

  async createPublisher(data: CreatePublisher): Promise<PublisherResponse> {
    return this.request<PublisherResponse>('/publishers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Campaigns
  async getCampaigns(filters?: {
    flowKind?: 'coreg' | 'hlp';
    status?: string;
    advertiserId?: string;
  }): Promise<CampaignDetailResponse[]> {
    const params = new URLSearchParams();
    if (filters?.flowKind) params.set('flowKind', filters.flowKind);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.advertiserId) params.set('advertiserId', filters.advertiserId);
    const qs = params.toString();
    return this.request<CampaignDetailResponse[]>(`/campaigns${qs ? `?${qs}` : ''}`);
  }

  async getCampaign(id: string): Promise<CampaignDetailResponse> {
    return this.request<CampaignDetailResponse>(`/campaigns/${id}`);
  }

  // Preview drafts (epic 037 task 05): unsaved-form CampaignResponse → token
  async createPreviewDraft(payload: CampaignResponse): Promise<PreviewDraftResponse> {
    return this.request<PreviewDraftResponse>('/preview-drafts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Campaign image assets (epic 095): the multipart upload behind the campaign
  // form's Image field. POST /admin/campaign-assets returns the minted public
  // URL (D1/D5) — the form collapses the chosen File into it; the campaign
  // keeps storing only banner_image_url.
  async uploadCampaignImage(file: File): Promise<CampaignAssetUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}${ADMIN_PREFIX}/campaign-assets`;
    beginRequest();
    try {
      let response: Response;
      try {
        response = await apiFetch(url, {
          method: 'POST',
          body: formData,
        });
      } catch {
        // The connection dropped mid-upload (browsers report this as a bare
        // "Failed to fetch" TypeError, with no HTTP status to read).
        throw new ApiError(
          0,
          'The image upload was interrupted — the connection dropped (the file may be too large, or the network failed). Try again.'
        );
      }

      if (!response.ok) {
        if (response.status === 413) {
          // nginx rejects an oversize body before it reaches the API, with its
          // own HTML error page (no JSON envelope to read a message from). The
          // API's own cap is 5 MB (095 D4) — a 413 means the browser pushed
          // past the proxy limit entirely, still an oversize image.
          throw new ApiError(413, 'The image is too large to upload (max 5 MB).');
        }
        const errorData = await response.json().catch(() => ({}));
        const message =
          typeof errorData.message === 'string' && errorData.message.length > 0
            ? errorData.message
            : response.statusText;
        throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
      }

      const text = await response.text();
      const envelope: ApiResponse<CampaignAssetUploadResponse> = JSON.parse(text);
      return envelope.data;
    } finally {
      endRequest();
    }
  }

  // Suppression
  async getSuppressionFiles(campaignId: string): Promise<SuppressionFileResponse[]> {
    return this.request<SuppressionFileResponse[]>(`/campaigns/${campaignId}/suppression-files`);
  }

  // Un-nested global listing (task 069-02, D3): every file with advertiser name
  // and attached campaigns; the screen's three filters run client-side on it.
  async getSuppressionFileList(): Promise<SuppressionFileListRowResponse[]> {
    return this.request<SuppressionFileListRowResponse[]>('/suppression-files');
  }

  async uploadSuppressionFile(campaignId: string, file: File): Promise<SuppressionFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}${ADMIN_PREFIX}/campaigns/${campaignId}/suppression-files`;
    beginRequest();
    try {
      let response: Response;
      try {
        response = await apiFetch(url, {
          method: 'POST',
          body: formData,
        });
      } catch {
        // The connection dropped mid-upload (browsers report this as a bare
        // "Failed to fetch" TypeError, with no HTTP status to read).
        throw new ApiError(
          0,
          'The suppression file upload was interrupted — the connection dropped (the file may be too large, or the network failed). Try again.'
        );
      }

      if (!response.ok) {
        if (response.status === 413) {
          // nginx rejects an oversize body before it reaches the API, with its
          // own HTML error page (no JSON envelope to read a message from).
          throw new ApiError(413, 'The suppression file is too large to upload (max 100 MB).');
        }
        const errorData = await response.json().catch(() => ({}));
        const message =
          typeof errorData.message === 'string' && errorData.message.length > 0
            ? errorData.message
            : response.statusText;
        throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
      }

      const envelope = (await response.json()) as ApiResponse<SuppressionFileResponse>;
      return envelope.data;
    } finally {
      endRequest();
    }
  }

  async deleteSuppressionFile(campaignId: string, fileId: string): Promise<void> {
    await this.request<void>(`/campaigns/${campaignId}/suppression-files/${fileId}`, {
      method: 'DELETE',
      allowEmptyBody: true,
    });
  }

  // Sites (reference data for the campaign form's restricted-site picker)
  async getSites(): Promise<SiteResponse[]> {
    return this.request<SiteResponse[]>('/sites');
  }

  async createSite(data: CreateSite): Promise<SiteResponse> {
    return this.request<SiteResponse>('/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSite(id: string, data: Partial<CreateSite>): Promise<SiteResponse> {
    return this.request<SiteResponse>(`/sites/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteSite(id: string): Promise<void> {
    await this.request<void>(`/sites/${id}`, {
      method: 'DELETE',
      allowEmptyBody: true,
    });
  }

  // Promo types (CTO reference data — the utm_campaign picklist, epic 024)
  async getPromoTypes(): Promise<PromoTypeResponse[]> {
    return this.request<PromoTypeResponse[]>('/promo-types');
  }

  async createPromoType(data: CreatePromoType): Promise<PromoTypeResponse> {
    return this.request<PromoTypeResponse>('/promo-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePromoType(id: string, data: UpdatePromoType): Promise<PromoTypeResponse> {
    return this.request<PromoTypeResponse>(`/promo-types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePromoType(id: string): Promise<void> {
    await this.request<void>(`/promo-types/${id}`, {
      method: 'DELETE',
      allowEmptyBody: true,
    });
  }

  // Email allow list (Q93 — the EmailOversight bypass, epic 024 task 07)
  async getAllowList(): Promise<EmailAllowListResponse[]> {
    return this.request<EmailAllowListResponse[]>('/allow-list');
  }

  async createAllowList(data: CreateEmailAllowList): Promise<EmailAllowListResponse> {
    return this.request<EmailAllowListResponse>('/allow-list', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAllowList(id: string): Promise<void> {
    await this.request<void>(`/allow-list/${id}`, {
      method: 'DELETE',
      allowEmptyBody: true,
    });
  }

  // CTO approved domains (epic 108 task 02 — the singleton raw comma string)
  async getCtoApprovedDomains(): Promise<CtoApprovedDomains> {
    return this.request<CtoApprovedDomains>('/cto/approved-domains');
  }

  async updateCtoApprovedDomains(data: CtoApprovedDomainsUpdate): Promise<CtoApprovedDomains> {
    return this.request<CtoApprovedDomains>('/cto/approved-domains', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // CTO links (epic 024 — the link rows under a cto_email campaign, FR-A34)
  async getCampaignLinks(campaignId: string): Promise<CampaignLinkRow[]> {
    return this.request<CampaignLinkRow[]>(`/campaigns/${campaignId}/links`);
  }

  async createCampaignLink(
    campaignId: string,
    data: CreateCampaignLinkInput
  ): Promise<CampaignLinkRow> {
    return this.request<CampaignLinkRow>(`/campaigns/${campaignId}/links`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaignLink(
    campaignId: string,
    linkId: string,
    data: UpdateCampaignLinkInput
  ): Promise<CampaignLinkRow> {
    return this.request<CampaignLinkRow>(`/campaigns/${campaignId}/links/${linkId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // CTO link directory (task 064-01 — every CTO link across every campaign,
  // filterable by traffic source; Chelsie's QA-07).
  async getCampaignLinkDirectory(siteId?: string): Promise<CampaignLinkDirectoryRow[]> {
    const qs = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
    return this.request<CampaignLinkDirectoryRow[]>(`/campaign-links${qs}`);
  }

  // Page Order overview (task 071-01 — every custom page by sequence and spot).
  async getPageOrder(): Promise<PageOrderResponse> {
    return this.request<PageOrderResponse>('/page-order');
  }

  // Page Order reorder (task 071-03 — save one sequence's pages in this order).
  async reorderPageSequence(
    sequence: number,
    pages: PageReorderItem[]
  ): Promise<PageOrderResponse> {
    return this.request<PageOrderResponse>(`/page-order/sequences/${sequence}`, {
      method: 'PUT',
      body: JSON.stringify({ pages }),
    });
  }

  // Page Order pin (task 077-03 — one pinned page per sequence; both answer
  // with the refreshed overview).
  async setPagePin(sequence: number, campaignId: string): Promise<PageOrderResponse> {
    return this.request<PageOrderResponse>(`/page-order/pins/${sequence}`, {
      method: 'PUT',
      body: JSON.stringify({ campaignId }),
    });
  }

  async clearPagePin(sequence: number): Promise<PageOrderResponse> {
    return this.request<PageOrderResponse>(`/page-order/pins/${sequence}`, {
      method: 'DELETE',
    });
  }

  async deleteCampaignLink(campaignId: string, linkId: string): Promise<void> {
    await this.request<void>(`/campaigns/${campaignId}/links/${linkId}`, {
      method: 'DELETE',
      allowEmptyBody: true,
    });
  }

  // CTO link stats (task 024-09 — date-range stats for the CTO links section)
  async getCampaignLinkStats(
    campaignId: string,
    from?: string,
    to?: string
  ): Promise<CampaignLinkStatsResponse> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return this.request<CampaignLinkStatsResponse>(
      `/campaigns/${campaignId}/links/stats${qs ? `?${qs}` : ''}`
    );
  }

  // Publisher uniques stats (task 024-09 — date-range stats for the Sites screen)
  async getSiteStats(from?: string, to?: string): Promise<SiteStatsResponse> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return this.request<SiteStatsResponse>(`/sites/stats${qs ? `?${qs}` : ''}`);
  }

  // Campaign aggregate write + lifecycle (task-03 / task-05 routes)
  async createCampaign(data: CampaignAggregateCreateInput): Promise<CampaignResponse> {
    return this.request<CampaignResponse>('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaign(id: string, data: CampaignAggregateUpdateInput): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async pauseCampaign(id: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/pause`, {
      method: 'POST',
    });
  }

  async resumeCampaign(id: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/resume`, {
      method: 'POST',
    });
  }

  async reopenCampaign(id: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/reopen`, {
      method: 'POST',
    });
  }

  async archiveCampaign(id: string): Promise<CampaignDetailResponse> {
    return this.request<CampaignDetailResponse>(`/campaigns/${id}/archive`, {
      method: 'POST',
    });
  }

  async unarchiveCampaign(id: string): Promise<CampaignDetailResponse> {
    return this.request<CampaignDetailResponse>(`/campaigns/${id}/unarchive`, {
      method: 'POST',
    });
  }

  async activateCampaign(id: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/activate`, {
      method: 'POST',
    });
  }

  async scheduleCampaign(id: string, scheduleDate: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduleDate }),
    });
  }

  async completeCampaign(id: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/complete`, {
      method: 'POST',
    });
  }

  async cloneCampaign(id: string): Promise<CampaignResponse> {
    return this.request<CampaignResponse>(`/campaigns/${id}/clone`, {
      method: 'POST',
    });
  }

  // Leads (read-only, task 016-05; link label epic 024 task 14; filters epic 088)
  async getLeads(filters?: {
    campaignId?: string;
    limit?: number;
    from?: string;
    to?: string;
    source?: string;
    includeTest?: boolean;
  }): Promise<LeadListWithLink> {
    const params = new URLSearchParams();
    if (filters?.campaignId) params.set('campaignId', filters.campaignId);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.source) params.set('source', filters.source);
    if (filters?.includeTest !== undefined) params.set('includeTest', String(filters.includeTest));
    const qs = params.toString();
    return this.request<LeadListWithLink>(`/leads${qs ? `?${qs}` : ''}`);
  }

  /**
   * Build the download URL for the Leads screen export (epic 088 D8): every
   * lead matching the filters, `all` rows or `unique` people. Dates go out
   * only as a pair (the api answers 400 to one alone); `source` is a site id
   * or `none`. The browser navigates to this URL directly — no fetch needed.
   */
  leadsExportUrl(filters: {
    from?: string;
    to?: string;
    source?: string;
    includeTest: boolean;
    variant: 'all' | 'unique';
  }): string {
    const params = new URLSearchParams();
    if (filters.from && filters.to) {
      params.set('from', filters.from);
      params.set('to', filters.to);
    }
    if (filters.source) params.set('source', filters.source);
    params.set('includeTest', String(filters.includeTest));
    params.set('variant', filters.variant);
    return `${this.baseUrl}${ADMIN_PREFIX}/leads/export.csv?${params.toString()}`;
  }

  /**
   * Build the download URL for the campaign CTO reconciliation CSV (task 024-13).
   * The browser navigates to this URL directly — no fetch needed.
   */
  campaignExportUrl(campaignId: string, from: string, to: string): string {
    const params = new URLSearchParams({ from, to });
    return `${this.baseUrl}${ADMIN_PREFIX}/campaigns/${campaignId}/cto-export.csv?${params.toString()}`;
  }

  /**
   * Build the download URL for the site CTO reconciliation CSV (task 024-13).
   * The browser navigates to this URL directly — no fetch needed.
   */
  siteExportUrl(siteId: string, from: string, to: string): string {
    const params = new URLSearchParams({ from, to });
    return `${this.baseUrl}${ADMIN_PREFIX}/sites/${siteId}/cto-export.csv?${params.toString()}`;
  }

  // Capture rejections (read-only, epic 024 task 14 — the Rejected view)
  async getCaptureRejections(filters?: {
    campaignId?: string;
    limit?: number;
  }): Promise<CaptureRejectionList> {
    const params = new URLSearchParams();
    if (filters?.campaignId) params.set('campaignId', filters.campaignId);
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return this.request<CaptureRejectionList>(`/capture-rejections${qs ? `?${qs}` : ''}`);
  }
}

export const api = new ApiClient();
